import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Link,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import RestoreFromTrashOutlinedIcon from '@mui/icons-material/RestoreFromTrashOutlined';
import { toast } from 'react-hot-toast';
import Header from 'shared/components/Header';
import LoadingSpinner from 'shared/components/LoadingSpinner';
import Table from 'shared/components/Table';
import { tokens } from 'shared/theme';
import { useAuth } from 'shared/hooks/useAuth';
import { canViewAllRecords } from 'shared/utils/canViewAllRecords';
import { listStatusParams, sopDocumentApi, systemDocumentApi } from '../api/systemRegisterApi';
import { formatEnumLabel } from '../constants/enums';
import { apiErrorMessage, unwrapData, unwrapList } from '../utils/responseHelpers';
import SystemDocumentDialog, { emptySystemDocumentRow } from '../shared/SystemDocumentDialog';
import { renderDeploymentStatusChip, renderRecordStatusChip } from '../shared/statusChips';
import { sortRowsNewestFirst } from 'shared/utils/sortRowsNewestFirst';

const isDeleted = (row) => String(row?.status || '').toUpperCase() === 'DELETED';

export default function SopPage() {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const { user } = useAuth();
  const canViewAll = canViewAllRecords(user);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAll, setShowAll] = useState(false);
  const [page, setPage] = useState(0);
  const [size] = useState(10);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [removeTarget, setRemoveTarget] = useState(null);
  const [removing, setRemoving] = useState(false);
  const [openingId, setOpeningId] = useState(null);
  const [restoringId, setRestoringId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const response = await sopDocumentApi.list(listStatusParams(canViewAll && showAll));
      const list = unwrapList(response);
      setRows(
        (Array.isArray(list) ? list : []).map((item) => ({
          ...item,
          id: item.id,
          status: item.status || 'ACTIVE',
        }))
      );
    } catch (error) {
      toast.error(apiErrorMessage(error, 'Failed to load documents.'));
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [canViewAll, showAll]);

  useEffect(() => {
    if (!canViewAll && showAll) {
      setShowAll(false);
    }
  }, [canViewAll, showAll]);

  useEffect(() => {
    load();
  }, [load]);

  const openAdd = () => {
    setEditing(emptySystemDocumentRow('itDocuments'));
    setDialogOpen(true);
  };

  const openEdit = (row) => {
    setEditing({ ...emptySystemDocumentRow('itDocuments'), ...row });
    setDialogOpen(true);
  };

  const handleSave = async (values) => {
    setSaving(true);
    try {
      const softwareId = Number(values.softwareId);
      const linked = Boolean(values.relatedToSoftware && Number.isFinite(softwareId) && softwareId > 0);
      const payload = {
        documentName: values.documentName?.trim(),
        fileName: values.fileName?.trim() || null,
        contentType: values.contentType || null,
        documentType: values.documentType || 'IT_DOCUMENT',
        softwareId: linked ? softwareId : null,
        deploymentStatus: linked ? (values.deploymentStatus || null) : null,
        location: values.location?.trim(),
      };
      if (values.id) {
        await sopDocumentApi.update(values.id, payload);
        toast.success('Document updated');
      } else {
        const created = unwrapData(await sopDocumentApi.create(payload));
        if (created?.id) {
          setRows((prev) => [
            { ...created, id: created.id },
            ...prev.filter((row) => row.id !== created.id),
          ]);
        }
        toast.success('Document added');
      }
      setDialogOpen(false);
      setEditing(null);
      setPage(0);
      await load();
    } catch (error) {
      toast.error(apiErrorMessage(error, 'Failed to save document.'));
    } finally {
      setSaving(false);
    }
  };

  const confirmRemove = async () => {
    if (!removeTarget?.id) return;
    setRemoving(true);
    try {
      await sopDocumentApi.remove(removeTarget.id);
      toast.success('Document marked as deleted');
      setRemoveTarget(null);
      await load();
    } catch (error) {
      toast.error(apiErrorMessage(error, 'Failed to delete document.'));
    } finally {
      setRemoving(false);
    }
  };

  const handleRestore = async (row) => {
    if (!canViewAll || !row?.id) return;
    setRestoringId(row.id);
    try {
      await sopDocumentApi.restore(row.id);
      toast.success('Document restored');
      await load();
    } catch (error) {
      toast.error(apiErrorMessage(error, 'Failed to restore document.'));
    } finally {
      setRestoringId(null);
    }
  };

  const handleOpenDocument = useCallback(async (row) => {
    if (!row?.location) {
      toast.error('No file attached to open.');
      return;
    }
    setOpeningId(row.id);
    try {
      const response = await systemDocumentApi.fetchFile(row.location);
      const contentType =
        row.contentType
        || response.headers?.['content-type']
        || 'application/octet-stream';
      const blob = new Blob([response.data], { type: contentType });
      const url = URL.createObjectURL(blob);
      const opened = window.open(url, '_blank', 'noopener,noreferrer');
      if (!opened) {
        const anchor = document.createElement('a');
        anchor.href = url;
        anchor.target = '_blank';
        anchor.rel = 'noopener noreferrer';
        anchor.download = row.fileName || 'document';
        document.body.appendChild(anchor);
        anchor.click();
        anchor.remove();
      }
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch (error) {
      toast.error(apiErrorMessage(error, 'Failed to open document'));
    } finally {
      setOpeningId(null);
    }
  }, []);

  const pagedRows = useMemo(() => {
    const ordered = sortRowsNewestFirst(rows);
    const start = page * size;
    return ordered.slice(start, start + size);
  }, [page, rows, size]);

  const columns = useMemo(
    () => [
      {
        field: 'documentName',
        headerName: 'Document name',
        flex: 1.3,
        minWidth: 180,
      },
      {
        field: 'documentType',
        headerName: 'Document type',
        width: 150,
        valueFormatter: (params) => formatEnumLabel(params?.value) || '—',
      },
      {
        field: 'softwareName',
        headerName: 'Software',
        flex: 1,
        minWidth: 150,
        valueFormatter: (params) => params?.value || '—',
      },
      {
        field: 'deploymentStatus',
        headerName: 'Deployment status',
        width: 170,
        renderCell: renderDeploymentStatusChip,
      },
      ...(showAll && canViewAll
        ? [{
            field: 'status',
            headerName: 'Status',
            width: 130,
            renderCell: renderRecordStatusChip,
          }]
        : []),
      {
        field: 'fileName',
        headerName: 'Attached file',
        flex: 1.2,
        minWidth: 180,
        renderCell: ({ row }) => {
          const opening = openingId === row.id;
          if (!row.fileName) {
            return (
              <Typography variant="body2" color="text.secondary">
                No file
              </Typography>
            );
          }
          if (!row.location) {
            return <Typography variant="body2">{row.fileName}</Typography>;
          }
          return (
            <Link
              component="button"
              type="button"
              variant="body2"
              underline="hover"
              disabled={opening}
              onClick={() => handleOpenDocument(row)}
              sx={{
                cursor: opening ? 'wait' : 'pointer',
                textAlign: 'left',
                border: 'none',
                background: 'none',
                p: 0,
                font: 'inherit',
              }}
            >
              {opening ? 'Opening…' : row.fileName}
            </Link>
          );
        },
      },
      {
        field: 'actions',
        headerName: 'Actions',
        width: 120,
        sortable: false,
        filterable: false,
        renderCell: ({ row }) => {
          const deleted = isDeleted(row);
          return (
            <Box display="flex" alignItems="center" gap={0.25}>
              <Tooltip title={deleted ? 'Restore before editing' : 'Edit document'}>
                <span>
                  <IconButton
                    size="small"
                    color="primary"
                    onClick={() => openEdit(row)}
                    disabled={deleted}
                    aria-label="Edit"
                  >
                    <EditOutlinedIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
              {deleted && canViewAll ? (
                <Tooltip title="Restore document">
                  <span>
                    <IconButton
                      size="small"
                      color="success"
                      onClick={() => handleRestore(row)}
                      disabled={restoringId === row.id}
                      aria-label="Restore"
                    >
                      <RestoreFromTrashOutlinedIcon fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
              ) : (
                <Tooltip title={deleted ? 'Already deleted' : 'Mark as deleted'}>
                  <span>
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => setRemoveTarget(row)}
                      disabled={deleted}
                      aria-label="Delete"
                    >
                      <DeleteOutlineIcon fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
              )}
            </Box>
          );
        },
      },
    ],
    [canViewAll, handleOpenDocument, openingId, restoringId, showAll]
  );

  return (
    <Box m="20px">
      <Header
        title="IT Documents"
        subtitle="Store and manage IT documents, with or without a linked software system"
      />

      <Box
        mb={2}
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        gap={1}
        flexWrap="wrap"
      >
        {canViewAll ? (
          <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
            <Button
              color="secondary"
              variant={showAll ? 'contained' : 'outlined'}
              size="small"
              sx={{ fontSize: '13px', py: 0.75, px: 1.5 }}
              onClick={() => {
                setShowAll(true);
                setPage(0);
              }}
            >
              View all
            </Button>
            <Button
              color="secondary"
              variant={!showAll ? 'contained' : 'outlined'}
              size="small"
              sx={{ fontSize: '13px', py: 0.75, px: 1.5 }}
              onClick={() => {
                setShowAll(false);
                setPage(0);
              }}
            >
              Active only
            </Button>
          </Box>
        ) : (
          <Box />
        )}
        <Button
          color="secondary"
          variant="contained"
          size="small"
          startIcon={<AddIcon />}
          sx={{ fontSize: '13px', py: 0.75, px: 1.5 }}
          onClick={openAdd}
          disabled={saving}
        >
          Add IT Documents
        </Button>
      </Box>

      {loading ? (
        <LoadingSpinner message="Loading documents…" height="300px" />
      ) : (
        <Table
          tableId="system-register.sop-documents"
          title="IT Documents"
          rows={pagedRows}
          columns={columns}
          totalRecords={rows.length}
          size={size}
          page={page}
          handlePageChange={setPage}
          colors={colors}
        />
      )}

      <SystemDocumentDialog
        open={dialogOpen}
        initialValues={editing}
        onClose={() => {
          if (saving) return;
          setDialogOpen(false);
          setEditing(null);
        }}
        onSave={handleSave}
        saving={saving}
        requireDeploymentStatus={false}
        variant="itDocuments"
      />

      <Dialog open={Boolean(removeTarget)} onClose={() => !removing && setRemoveTarget(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Delete document?</DialogTitle>
        <DialogContent>
          <Typography variant="body2">Do you want to delete the document?</Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setRemoveTarget(null)} disabled={removing}>
            Cancel
          </Button>
          <Button color="error" variant="contained" onClick={confirmRemove} disabled={removing}>
            {removing ? 'Deleting…' : 'Confirm'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
